import { useEffect, useMemo, useState } from "react";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Smartphone, CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { priceVal, fmtPrice } from "@/lib/currency";

const usePreviewState = (open) => {
  const [st, setSt] = useState(null);
  useEffect(() => {
    const on = (e) => setSt(e.detail);
    window.addEventListener("preview-state", on);
    return () => window.removeEventListener("preview-state", on);
  }, []);
  return open ? st : null;
};

export const SubscribeDialog = ({ plan, currency = "RWF", currencies = [], open, onClose, stay = false }) => {
  const preview = usePreviewState(open);
  const { refresh, isAuthed } = useAuth();
  const [gateway, setGateway] = useState("momo");
  const [cur, setCur] = useState(typeof currency === "string" ? currency : currency?.code || "RWF");
  const [momoPhone, setMomoPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pendingRef, setPendingRef] = useState("");
  const [checking, setChecking] = useState(false);

  const momoOnly = !!plan?.rwfOnly;  // daily / weekly plans: RWF + Mobile Money only
  const code = momoOnly ? "RWF" : (cur || "RWF").toUpperCase();
  const isRwf = code === "RWF";
  const momoValid = /^7\d{8}$/.test(momoPhone);
  const curObj = useMemo(() => currencies.find((c) => c.code === code) || { code, symbol: code }, [currencies, code]);
  const enabledMethods = usePaymentMethods("memberships", open);
  const gatewayIds = useMemo(() => {
    const all = [{ id: "momo", show: isRwf }, { id: "paypal", show: !momoOnly }, { id: "stripe", show: !momoOnly }];
    return all.filter((g) => g.show && (!enabledMethods || enabledMethods.includes(g.id))).map((g) => g.id);
  }, [isRwf, enabledMethods, momoOnly]);
  // MoMo is RWF-only. PayPal (PayPal account OR any debit/credit card) is the main card option.

  useEffect(() => {
    if (open) {
      const c = (typeof currency === "string" ? currency : currency?.code || "RWF").toUpperCase();
      setCur(c);
      setGateway(c === "RWF" ? "momo" : "paypal");
      setBusy(false); setPolling(false); setPendingRef("");
    }
  }, [open, currency]);
  // If the admin switched off the selected method, move to the first available one.
  useEffect(() => {
    if (gatewayIds.length && !gatewayIds.includes(gateway)) setGateway(gatewayIds[0]);
  }, [gatewayIds, gateway]);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, busy, onClose]);

  if (!plan) return null;

  const priceLabel = fmtPrice(priceVal(plan, code), curObj);
  const rwfLabel = `${Number(priceVal(plan, "RWF")).toLocaleString()} RWF`;

  const handlePaid = async () => {
    await refresh();
    toast.success("Payment successful — welcome aboard! 🎉");
    setPolling(false); setChecking(false); onClose();
    if (!stay) window.location.href = `${window.location.origin}/payment/success?provider=momo`;
  };

  const checkStatus = async () => {
    if (!pendingRef) return;
    setChecking(true);
    try {
      const { data } = await endpoints.momoStatus(pendingRef);
      if (data.paid || data.status === "successful") { await handlePaid(); return; }
      if (data.status === "failed") { toast.error("Payment failed or was declined. You can try again."); setChecking(false); return; }
      toast.message("Your payment is still being confirmed. Please do not pay again yet.");
    } catch { toast.error("Could not check status. Please try again in a moment."); }
    setChecking(false);
  };

  const pollMomo = async (ref) => {
    setPendingRef(ref);
    setPolling(true);
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { data } = await endpoints.momoStatus(ref);
        if (data.paid || data.status === "successful") { await handlePaid(); return; }
        if (data.status === "failed") { toast.error("Payment failed or was declined."); setPolling(false); return; }
      } catch { /* keep polling */ }
    }
    setPolling(false);
    toast.message("Still pending — tap “Check payment status” if your money was deducted.");
  };

  const pay = async () => {
    if (!isAuthed) { toast.error("Please sign in first"); return; }
    setBusy(true);
    try {
      if (gateway === "stripe") {
        const origin = window.location.origin;
        const { data } = await endpoints.stripeCheckout({
          purchase_type: "subscription", plan: plan.id, currency: cur.toLowerCase(),
          success_url: `${origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/pricing`,
        });
        window.location.href = data.url || data.checkout_url;
      } else if (gateway === "paypal") {
        setBusy(false);
        return; // PayPal renders its own secure buttons below (card/guest incl.)
      } else {
        if (!momoValid) { toast.error("Enter a valid Rwanda MoMo number (9 digits starting with 7)"); setBusy(false); return; }
        const { data } = await endpoints.momoInitiate(plan.id, `+250${momoPhone}`);
        toast.success(`Approve the payment on +250 ${momoPhone} (MoMo PIN prompt)…`);
        await pollMomo(data.reference);
      }
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const paypalLabel = fmtPrice(priceVal(plan, isRwf ? "USD" : code), isRwf ? { code: "USD", symbol: "$" } : curObj);
  const allGateways = [
    { id: "momo", label: "MTN MoMo", desc: `Mobile Money · billed in RWF (${rwfLabel})`, icon: Smartphone, show: isRwf },
    { id: "paypal", label: "Debit / Credit Card or PayPal", desc: `Enter your card right here · ${paypalLabel} · no PayPal account needed`, icon: CreditCard, show: true, main: true },
    { id: "stripe", label: "Other card (Stripe)", desc: `Visa / Mastercard · ${priceLabel}`, icon: CreditCard, show: true },
  ];
  const gateways = allGateways.filter((g) => g.show && gatewayIds.includes(g.id));

  const payLabel = gateway === "momo" ? rwfLabel : gateway === "paypal" ? paypalLabel : priceLabel;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="subscribe-dialog">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !busy && onClose()} />
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            className="relative w-full max-w-md glass border border-[#273042] rounded-2xl p-7 shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => !busy && onClose()} className="absolute right-4 top-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            <h2 className="text-2xl font-extrabold text-white">Complete your subscription</h2>
            {preview && (preview.phase === "grace" || preview.phase === "free") && (
              <p className="mt-3 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="subscribe-preview-note">
                {preview.phase === "grace"
                  ? <>Your free preview has ended. Your {preview.stream === "tv" ? "Live TV" : "radio"} keeps playing for <b data-testid="subscribe-grace-left">{Math.floor(preview.graceLeft / 60)}:{String(preview.graceLeft % 60).padStart(2, "0")}</b> while you pay.</>
                  : <>Free preview running — subscribe now for unlimited live {preview.stream === "tv" ? "TV" : "radio"}.</>}
              </p>
            )}
            {preview?.phase === "expired" && (
              <p className="mt-3 text-xs text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="subscribe-preview-expired">Your free preview for today has ended. Subscribe to continue watching and listening.</p>
            )}

            {momoOnly && <p className="mt-4 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="momo-only-note">Daily and weekly plans are paid in Rwandan francs (RWF) with MTN Mobile Money only.</p>}
            {!momoOnly && <><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-5 mb-2">Currency</div>
            <div className="flex gap-2 flex-wrap" data-testid="currency-toggle">
              {(currencies.some((c) => c?.code) ? currencies.filter((c) => c?.code) : [{ code: "RWF", symbol: "RWF" }]).map((c) => (
                <button key={c.code} onClick={() => { setCur(c.code); setGateway(c.code === "RWF" ? "momo" : "paypal"); }} data-testid={`currency-${c.code}`}
                  className={`flex-1 min-w-[72px] h-10 rounded-lg text-sm font-bold border transition-colors ${code === c.code ? "border-[#E11D2A] bg-[#E11D2A]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300 hover:text-white"}`}>
                  {c.symbol !== c.code ? `${c.symbol} ` : ""}{c.code}
                </button>
              ))}
            </div>
            {!isRwf && <p className="mt-2 text-[11px] text-slate-400">Non-RWF payments are processed by PayPal (any card) or Stripe. MTN MoMo is available for RWF only.</p>}</>}


            <div className="mt-4 flex items-baseline justify-between rounded-xl bg-[#0A0C10] border border-[#273042] p-4">
              <div>
                <div className="text-white font-bold">{plan.label}</div>
                <div className="text-xs text-slate-400 capitalize">{plan.tier} · {plan.period}</div>
              </div>
              <div className="text-2xl font-extrabold text-[#E11D2A]" data-testid="subscribe-price">{payLabel}</div>
            </div>

            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-6 mb-2">Payment method</div>
            <div className="space-y-2">
              {gateways.map((g) => (
                <button key={g.id} onClick={() => setGateway(g.id)} data-testid={`gateway-${g.id}`}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-colors text-left ${gateway === g.id ? "border-[#E11D2A] bg-[#E11D2A]/10" : "border-[#273042] bg-[#0A0C10] hover:border-slate-600"}`}>
                  <g.icon className="w-5 h-5 text-slate-300" />
                  <div className="flex-1">
                    <div className="text-white text-sm font-semibold flex items-center gap-2">{g.label}{g.main && <span className="text-[9px] font-extrabold uppercase bg-[#2563EB] text-white px-1.5 py-0.5 rounded" data-testid="gateway-paypal-main-badge">Main card option</span>}</div>
                    <div className="text-xs text-slate-400">{g.desc}</div>
                  </div>
                  {gateway === g.id && <Check className="w-4 h-4 text-[#E11D2A]" />}
                </button>
              ))}
            </div>

            {gateway === "momo" && (
              <div className="mt-3" data-testid="momo-phone-group">
                <label className="text-xs text-slate-400 block mb-1">MoMo phone number (Rwanda)</label>
                <div className="flex items-stretch rounded-xl bg-[#0A0C10] border border-[#273042] overflow-hidden focus-within:border-[#E11D2A]/60">
                  <span className="flex items-center gap-1 px-3 text-slate-300 font-semibold border-r border-[#273042] bg-white/5 select-none">🇷🇼 +250</span>
                  <input value={momoPhone} onChange={(e) => setMomoPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 9))} data-testid="momo-phone-input"
                    placeholder="788 123 456" inputMode="numeric" maxLength={9}
                    className="flex-1 h-12 bg-transparent px-3 text-white outline-none tracking-wide" />
                </div>
                {momoPhone.length > 0 && !momoValid && (
                  <p className="mt-1.5 text-xs text-red-400" data-testid="momo-phone-error">Enter 9 digits starting with 7 (e.g. 788 123 456).</p>
                )}
                {momoValid && (
                  <p className="mt-1.5 text-xs text-emerald-400" data-testid="momo-phone-confirm">Payment will be charged to <b>+250 {momoPhone}</b></p>
                )}
              </div>
            )}

            {gateway === "paypal" ? (
              <PayPalCheckout key={`${plan.id}-${cur}`} plan={plan} currency={cur} amountLabel={paypalLabel}
                onSuccess={async () => {
                  await refresh(); onClose();
                  if (stay) toast.success("Payment successful — full access unlocked!");
                  else window.location.href = `${window.location.origin}/payment/success?provider=paypal`;
                }} />
            ) : (
              <>
                <button onClick={pay} disabled={busy || polling} data-testid="momo-payment-submit-btn"
                  className="mt-6 w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {(busy || polling) ? <><Loader2 className="w-5 h-5 animate-spin" /> {polling ? "Waiting for approval…" : "Processing…"}</> : `Pay ${payLabel}`}
                </button>
                {pendingRef && (
                  <button onClick={checkStatus} disabled={checking} data-testid="check-payment-status-btn"
                    className="mt-3 w-full h-11 rounded-xl border-2 border-[#E11D2A] text-[#E11D2A] font-bold flex items-center justify-center gap-2 hover:bg-[#E11D2A]/10 transition-colors disabled:opacity-60">
                    {checking ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Check payment status"}
                  </button>
                )}
                {pendingRef && !polling && (
                  <p className="mt-2 text-[11px] text-amber-400/90 text-center">If your money was deducted, tap “Check payment status”. Please don’t pay again yet.</p>
                )}
              </>
            )}
            <p className="mt-3 text-[11px] text-slate-400 text-center" data-testid="no-auto-renew-note">One-time payment · no automatic renewal · your card is never charged again automatically. We'll remind you by SMS before it ends.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
